/**
 * Shared HTTP retry helpers for authenticated storage writes and proxied
 * provider fetches. Centralises backoff + jitter so sedBootstrap GCS saves
 * and federal-dockets sub-fetchers behave consistently.
 */

export const RETRYABLE_HTTP_STATUS = new Set([429, 500, 502, 503, 504]);

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Exponential backoff with ±20% jitter.
 * 429 uses a longer base because server-side IP buckets reset every 60s.
 */
export function retryBackoffMs(attempt: number, status?: number): number {
  const is429 = status === 429;
  const base = is429 ? 2_000 : 500;
  const cap = is429 ? 30_000 : 8_000;
  const exp = Math.min(base * 2 ** (attempt - 1), cap);
  const jitter = exp * (0.8 + Math.random() * 0.4);
  return Math.round(jitter);
}

/** Parse Retry-After (seconds or HTTP-date). Returns null when absent/invalid. */
export function parseRetryAfterMs(header: string | null | undefined): number | null {
  if (!header) return null;
  const sec = Number(header);
  if (Number.isFinite(sec) && sec > 0) return sec * 1000;
  const date = Date.parse(header);
  if (Number.isFinite(date)) return Math.max(0, date - Date.now());
  return null;
}

export interface HttpRetryOptions {
  maxAttempts?: number;
  label?: string;
  /** When true, log retry warnings to console. */
  log?: boolean;
}

/**
 * Resolve wait duration for a retryable HTTP status. Prefers Retry-After
 * when present; otherwise uses exponential backoff + jitter.
 */
export function resolveRetryWaitMs(
  attempt: number,
  status: number,
  retryAfterHeader: string | null | undefined,
): number {
  const retryAfter = parseRetryAfterMs(retryAfterHeader);
  if (retryAfter != null && retryAfter > 0) return retryAfter;
  return retryBackoffMs(attempt, status);
}
